import * as React from "react"
import H2 from "../../../atoms/typography/H2"
import BundleSuggestedSection from "../../../store/solution/BundleSuggestedSection"
import { Product } from "../../../interfaces/SaleorTypes"
import { Col, Row } from "react-grid-system"
import { HorizontalSpacer } from "../../../atoms/spacers"
import SatcubeStarterPack from "../../../molecules/store/CustomizeAndOrder/SatcubeStarterPack"
import styled from "styled-components"
import H4 from "../../../atoms/typography/H4"
import SmallColorfulButton from "../../../atoms/buttons/SmallColorfulButton"
import { ArrowRight } from "../../../atoms/icons/ArrowRight"
import AddedAccessoriesRow from "../../../molecules/store/CustomizeAndOrder/AddedAccessoriesRow"
import { theme } from "../../../theme"
import { getFormattedPrice } from "../../../../utils/cart-helpers"

interface DefaultValues {
  accessories: Array<{ product: Product; amount: number }>
}

interface Props {
  onClickOrderOverview: (items: Array<{ product: Product; amount: number }>, total: number) => void
  defaultValues: DefaultValues
  terminalTotal: number
  dpTotal: number
  currency?: string
}

const FlexedText = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CustomizeAccessories: React.FC<Props> = ({
  onClickOrderOverview,
  defaultValues,
  terminalTotal,
  dpTotal,
  currency,
}) => {
  const [selectedAccessories, setSelectedAccessories] = React.useState<Array<{ product: Product; amount: number }>>(
    defaultValues.accessories,
  )
  const isKu = window.location.href.includes("ku")

  const onClickAddItem = (item: Product, amount = 1) => {
    setSelectedAccessories((prevSelectedAccessories) => {
      const existingItem = prevSelectedAccessories.find((x) => x.product.id === item.id)

      if (existingItem) {
        // If the item already exists, increase the amount
        return prevSelectedAccessories.map((x) => (x.product.id === item.id ? { ...x, amount: x.amount + 1 } : x))
      } else {
        // If the item does not exist, add a new one
        return [...prevSelectedAccessories, { product: item, amount: amount }]
      }
    })
  }

  const onClickMinus = (productId: string) => {
    setSelectedAccessories((prevSelectedAccessories) =>
      prevSelectedAccessories.map((x) =>
        x.product.id === productId ? { ...x, amount: Math.max(x.amount - 1, 0) } : x,
      ),
    )
  }

  const onClickPlus = (productId: string) => {
    setSelectedAccessories((prevSelectedAccessories) =>
      prevSelectedAccessories.map((x) => (x.product.id === productId ? { ...x, amount: x.amount + 1 } : x)),
    )
  }

  const onClickRemove = (productId: string) => {
    setSelectedAccessories((prevSelectedAccessories) =>
      prevSelectedAccessories.filter((x) => x.product.id !== productId),
    )
  }

  const totalPrice = selectedAccessories.reduce(
    (acc, item) => acc + (item.product.variants[0].pricing.price.gross.amount ?? 0) * item.amount,
    0,
  )

  const onClickContinue = () => {
    onClickOrderOverview(selectedAccessories, totalPrice ?? 0)
  }

  return (
    <div>
      <Row>
        <Col lg={1} />
        <Col lg={11}>
          <H2>Suggested accessories</H2>
        </Col>
      </Row>
      <HorizontalSpacer spacing={1} />
      <BundleSuggestedSection
        onClickAddItem={onClickAddItem}
        gradient={isKu ? theme.gradients.blue : theme.gradients.green}
      />

      <HorizontalSpacer spacing={2} />
      <Row>
        <Col lg={1} />
        <Col lg={10}>
          <H2>Included accessories</H2>
          <HorizontalSpacer spacing={1.5} />
          <SatcubeStarterPack />
        </Col>
      </Row>

      <HorizontalSpacer spacing={2} />
      <Row>
        <Col lg={1} />
        <Col lg={10}>
          <H2>Additional accessories</H2>
          <HorizontalSpacer spacing={1.5} />
          {selectedAccessories.map((x) => (
            <div key={x.product.id}>
              <AddedAccessoriesRow
                title={x.product.name}
                imgSrc={x.product.media[0]?.url}
                amount={x.amount}
                onClickMinus={() => onClickMinus(x.product.id)}
                onClickPlus={() => onClickPlus(x.product.id)}
                onClickRemove={() => onClickRemove(x.product.id)}
                price={`${getFormattedPrice(
                  x.product.variants[0]?.pricing.price.gross.amount ?? 0,
                  x.product.variants[0]?.pricing.price.currency,
                )}`}
              />
              <HorizontalSpacer spacing={2} />
            </div>
          ))}
        </Col>
      </Row>

      <HorizontalSpacer spacing={2} />
      <Row>
        <Col lg={1} />
        <Col lg={10}>
          <FlexedText>
            <div>
              <H4>Total:</H4>
              <H4>{getFormattedPrice(totalPrice + terminalTotal + dpTotal, currency)}</H4>
            </div>
            <SmallColorfulButton
              title="Order overview"
              iconRight={<ArrowRight color="white" />}
              onClick={onClickContinue}
              variant={isKu ? "blue" : "green"}
            />
          </FlexedText>
        </Col>
      </Row>
      <HorizontalSpacer spacing={1.5} />
    </div>
  )
}

export default CustomizeAccessories
